import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Container, Factory, Building2, ShoppingBag, Hotel, Sun, Truck, HeartPulse, Theater, ShieldCheck, TrainFront } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/hooks/use-scroll-reveal";
import heroImg from "@/assets/hero-sez.jpg";
import dryPortImg from "@/assets/dry-port.jpg";
import financialImg from "@/assets/financial-center.jpg";
import solarImg from "@/assets/solar-plant.jpg";
import retailImg from "@/assets/retail-mall.jpg";
import hotelImg from "@/assets/hotel.jpg";
import manufacturingImg from "@/assets/manufacturing.jpg";
import logisticsImg from "@/assets/logistics.jpg";
import hospitalImg from "@/assets/hospital.jpg";
import conventionImg from "@/assets/convention-center.jpg";

import clipSezOverview from "@/assets/clips/clip-sez-overview.mp4";

const clipModules = [
() => clipSezOverview,
() => import("@/assets/clips/clip-dry-port.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-logistics.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-manufacturing.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-financial.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-medical.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-hospitality.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-retail.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-convention.mp4").then((m) => m.default),
() => import("@/assets/clips/clip-solar.mp4").then((m) => m.default)];


const sectorKeys = ["dryPort", "logistics", "manufacturing", "retail", "energy", "financial", "hospitality", "medical", "convention", "customs"] as const;
const sectorIcons = [Container, Truck, Factory, ShoppingBag, Sun, Building2, Hotel, HeartPulse, Theater, ShieldCheck];
const sectorPaths = ["/development/dry-port", "/development/logistics", "/development/manufacturing", "/development/retail", "/development/energy", "/development/financial-center", "/development/hospitality", "/development/medical", "/development/convention", "/development/customs"];
const sectorImgs = [dryPortImg, logisticsImg, manufacturingImg, retailImg, solarImg, financialImg, hotelImg, hospitalImg, conventionImg, dryPortImg];

const useParallax = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        setOffset(ref.current.getBoundingClientRect().top * 0.4);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return { ref, offset };
};

const statKeys = [
{ value: "2,000+", key: "hectares" },
{ value: "600 ha", key: "dryPort" },
{ value: "8M", key: "teuCapacity" },
{ value: "300 ha", key: "logisticsHub" },
{ value: "100 MW", key: "solarPower" },
{ value: "10", key: "hotels" }];


const Index = () => {
  const { t } = useTranslation();
  const hero = useParallax();
  const midSection = useParallax();
  const [visible, setVisible] = useState(false);
  const [clipIndex, setClipIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const videoRefA = useRef<HTMLVideoElement>(null);
  const videoRefB = useRef<HTMLVideoElement>(null);
  const [activeSlot, setActiveSlot] = useState<"A" | "B">("A");
  const clipCache = useRef<Record<number, string>>({ 0: clipSezOverview });

  const heroLabels = t("heroLabels", { returnObjects: true }) as string[];

  const getClipSrc = useCallback(async (index: number): Promise<string> => {
    if (clipCache.current[index]) return clipCache.current[index];
    const src = await clipModules[index]();
    clipCache.current[index] = src;
    return src;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    if (videoRefA.current) videoRefA.current.playbackRate = 0.5;
    if (videoRefB.current) videoRefB.current.playbackRate = 0.5;
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const nextIndex = (clipIndex + 1) % clipModules.length;
    const inactiveRef = activeSlot === "A" ? videoRefB : videoRefA;
    getClipSrc(nextIndex).then((src) => {
      if (inactiveRef.current) {
        inactiveRef.current.src = src;
        inactiveRef.current.load();
      }
    });
  }, [clipIndex, activeSlot, getClipSrc]);

  const advanceClip = useCallback(() => {
    setTimeout(() => {
      setFadeIn(false);
      const nextIndex = (clipIndex + 1) % clipModules.length;
      const nextSlot = activeSlot === "A" ? "B" : "A";
      const nextRef = nextSlot === "A" ? videoRefA : videoRefB;
      if (nextRef.current) {
        nextRef.current.playbackRate = 0.5;
        nextRef.current.play().catch(() => {});
      }
      setTimeout(() => {
        setClipIndex(nextIndex);
        setActiveSlot(nextSlot);
        setFadeIn(true);
      }, 1500);
    }, 3000);
  }, [clipIndex, activeSlot]);

  useEffect(() => {
    const activeRef = activeSlot === "A" ? videoRefA : videoRefB;
    const videoEl = activeRef.current;
    if (!videoEl) return;
    const handleEnded = () => advanceClip();
    videoEl.addEventListener("ended", handleEnded);
    return () => videoEl.removeEventListener("ended", handleEnded);
  }, [activeSlot, advanceClip]);

  return (
    <div>
      {/* Hero */}
      <div ref={hero.ref} className="relative h-screen overflow-hidden flex items-center">
        <video
          ref={videoRefA}
          autoPlay muted playsInline preload="auto"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${activeSlot === "A" && fadeIn ? "opacity-100" : "opacity-0"}`}
          style={{ transform: `translateY(${hero.offset}px)`, willChange: "transform" }}
          // @ts-ignore
          fetchpriority="high">
          
          <source src={clipSezOverview} type="video/mp4" />
        </video>
        <video
          ref={videoRefB}
          muted playsInline preload="auto"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${activeSlot === "B" && fadeIn ? "opacity-100" : "opacity-0"}`}
          style={{ transform: `translateY(${hero.offset}px)`, willChange: "transform" }} />
        
        <div className="absolute inset-0 overlay-gradient" />

        <div className="absolute bottom-24 left-6 md:left-12 lg:left-20 z-20">
          <p
            className={`text-sm md:text-base font-display tracking-[0.2em] uppercase text-foreground/90 transition-all duration-700 ${fadeIn ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.6)" }}>
            
            {heroLabels[clipIndex]}
          </p>
        </div>

        <div className="absolute top-6 right-6 z-20 flex gap-2">
          {heroLabels.map((label, i) =>
          <button
            key={i}
            onClick={() => {
              if (i === clipIndex) return;
              const nextSlot = activeSlot === "A" ? "B" : "A";
              const nextRef = nextSlot === "A" ? videoRefA : videoRefB;
              getClipSrc(i).then((src) => {
                if (nextRef.current) {
                  nextRef.current.src = src;
                  nextRef.current.load();
                }
                setFadeIn(false);
                if (nextRef.current) {
                  nextRef.current.playbackRate = 0.5;
                  nextRef.current.play().catch(() => {});
                }
                setTimeout(() => {
                  setClipIndex(i);
                  setActiveSlot(nextSlot);
                  setFadeIn(true);
                }, 1500);
              });
            }}
            className="p-2 transition-all duration-300"
            aria-label={label}>
            
              <span className={`block w-2 h-2 rounded-full transition-all duration-300 ${i === clipIndex ? "bg-primary w-6" : "bg-foreground/30 hover:bg-foreground/50"}`} />
            </button>
          )}
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto w-full">
          <div className={`max-w-2xl transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6 whitespace-pre-line">
              {t("hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80 mb-8 max-w-2xl font-body tracking-wide leading-relaxed font-semibold text-left whitespace-pre-line" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.7), 0 1px 4px rgba(0,0,0,0.5)" }}>
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs text-muted-foreground tracking-widest uppercase">{t("hero.scroll")}</span>
          <div className="w-px h-8 bg-primary/40 animate-pulse" />
        </div>
      </div>

      {/* Stats */}
      <section className="section-padding bg-card">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {statKeys.map((stat, i) =>
            <ScrollReveal key={i} delay={i * 100}>
                <div className="stat-card text-center">
                  <div className="text-2xl md:text-3xl font-display font-bold text-primary mb-1">{stat.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{t(`stats.${stat.key}`)}</div>
                </div>
              </ScrollReveal>
            )}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left">
            <p className="text-primary font-display text-sm tracking-[0.3em] uppercase mb-4">{t("about.label")}</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              {t("about.title")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">{t("about.p1")}</p>
            <p className="text-muted-foreground leading-relaxed mb-6">{t("about.p2")}</p>
            <p className="text-muted-foreground leading-relaxed mb-8">{t("about.p3")}</p>
            <Link to="/about/nsi" className="inline-flex items-center gap-2 text-primary hover:text-gold-light transition-colors font-display text-sm tracking-wider uppercase">
              {t("about.learnMore")} <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
          <ScrollReveal direction="right" delay={200}>
            <div className="relative">
              <img src={financialImg} alt="New Silkway Financial Center" className="rounded-lg w-full aspect-[4/3] object-cover" width={729} height={417} loading="lazy" />
              <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-lg hidden md:block">
                <div className="text-3xl font-display font-bold">2,000+</div>
                <div className="text-sm">{t("about.hectaresDev")}</div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Dry Port Feature */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left">
            <div className="relative">
              <img src={dryPortImg} alt="New Silkway Inland Dry Port" className="rounded-lg w-full aspect-[4/3] object-cover" width={735} height={417} loading="lazy" />
              <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-6 rounded-lg hidden md:block">
                <div className="text-3xl font-display font-bold">600 ha</div>
                <div className="text-sm">{t("dryPortSection.largestZone")}</div>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="right" delay={200}>
            <p className="text-primary font-display text-sm tracking-[0.3em] uppercase mb-4">{t("dryPortSection.label")}</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              {t("dryPortSection.title")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">{t("dryPortSection.desc")}</p>
            <div className="flex items-center gap-3 mb-6 text-primary">
              <TrainFront className="w-5 h-5" />
              <span className="text-sm font-display tracking-wider uppercase">{t("dryPortSection.railLine")}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="stat-card text-center">
                <div className="text-xl font-display font-bold text-primary">8M TEU</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">{t("dryPortSection.annualCapacity")}</div>
              </div>
              <div className="stat-card text-center">
                <div className="text-xl font-display font-bold text-primary">600 ha</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">{t("dryPortSection.totalFootprint")}</div>
              </div>
            </div>
            <Link to="/development/dry-port" className="inline-flex items-center gap-2 text-primary hover:text-gold-light transition-colors font-display text-sm tracking-wider uppercase">
              {t("dryPortSection.explore")} <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Parallax Mid */}
      <div ref={midSection.ref} className="relative h-[50vh] overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${solarImg})`, transform: `translateY(${midSection.offset}px)`, willChange: "transform" }} />
        
        <div className="absolute inset-0 overlay-dark" />
        <div className="relative z-10 text-center px-6">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4">{t("sustainable.title")}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t("sustainable.desc")}</p>
        </div>
      </div>

      {/* Development Sectors */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <p className="text-primary font-display text-sm tracking-[0.3em] uppercase mb-4">{t("sectors.label")}</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">{t("sectors.title")}</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectorKeys.map((key, i) => {
              const Icon = sectorIcons[i];
              return (
                <ScrollReveal key={i} delay={i % 3 * 150}>
                  <Link to={sectorPaths[i]} className="dev-card group aspect-[4/3]">
                    <img src={sectorImgs[i]} alt={t(`sectorCards.${key}.label`)} className="w-full h-full object-cover" loading="lazy" width={376} height={213} />
                    <div className="dev-card-overlay">
                      <Icon className="w-8 h-8 text-primary mb-3" />
                      <h3 className="text-lg font-display font-semibold text-foreground mb-1">{t(`sectorCards.${key}.label`)}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{t(`sectorCards.${key}.desc`)}</p>
                    </div>
                  </Link>
                </ScrollReveal>);

            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-card">
        <ScrollReveal>
          <div className="max-w-[1400px] mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              {t("cta.title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">{t("cta.desc")}</p>
            <Link
              to="/sez/investment"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-display text-sm font-semibold tracking-wider uppercase rounded-sm hover:bg-gold-dark transition-colors">
              
              {t("cta.button")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>);

};

export default Index;